import React, { useMemo } from "react";
import { useTranslate } from "react-admin";
import { PeriodType } from "../../../common/data/service-proxies";
import type { FuelConsumptionByTimeOfDayDto } from "../../../common/data/service-proxies";
import { startOfDay } from "date-fns";
import { formatTooltipAxis } from "../fuel-economy-report/chartUtils";
import { useProLogsTheme } from "../../../common/hooks/useProLogsTheme";
import ReactECharts from "echarts-for-react";

export interface MultiSeriesLineChartProps {
  data: FuelConsumptionByTimeOfDayDto;
  fromDate: Date;
  toDate: Date;
  periodType?: PeriodType;
}

const buildDateLabels = (
  from: Date,
  to: Date,
  periodType: PeriodType,
): string[] => {
  const result: string[] = [];
  const fromStart = startOfDay(from);
  const toStart = startOfDay(to);
  const oneDay = 24 * 60 * 60 * 1000;

  if (periodType === PeriodType.Weekly) {
    for (let d = fromStart.getTime(); d <= toStart.getTime(); d += oneDay) {
      result.push(new Date(d).toLocaleDateString());
    }
  } else {
    const days: Date[] = [];
    for (let d = fromStart.getTime(); d <= toStart.getTime(); d += oneDay) {
      days.push(new Date(d));
    }
    const byWeek: { [bucket: string]: number } = {};
    days.forEach((d) => {
      const dayOfMonth = d.getDate();
      const bucket = `${Math.floor((dayOfMonth - 1) / 7) + 1}`;
      byWeek[bucket] = 1;
    });
    Object.keys(byWeek)
      .sort((a, b) => Number(a) - Number(b))
      .forEach((b) => result.push(`W${b}`));
  }

  return result;
};

export const MultiSeriesLineChart: React.FC<MultiSeriesLineChartProps> = ({
  data,
  fromDate,
  toDate,
  periodType = PeriodType.Weekly,
}) => {
  const translate = useTranslate();
  const theme = useProLogsTheme();

  const chartOption = useMemo(() => {
    const labels = buildDateLabels(fromDate, toDate, periodType);

    const morningValues = data?.morningValues ?? [];
    const noonValues = data?.noonValues ?? [];
    const afternoonValues = data?.afternoonValues ?? [];
    const nightValues = data?.nightValues ?? [];

    return {
      tooltip: {
        trigger: "axis",
        formatter: formatTooltipAxis,
      },
      legend: {
        data: [
          translate("fuelEconomyReport.morning", { _: "Morning" }),
          translate("fuelEconomyReport.noon", { _: "Noon" }),
          translate("fuelEconomyReport.afternoon", { _: "Afternoon" }),
          translate("fuelEconomyReport.night", { _: "Night" }),
        ],
        bottom: 0,
        textStyle: {
          color: theme.palette.text.primary,
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "15%",
        top: "15%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: labels,
        boundaryGap: false,
        axisLabel: {
          color: theme.palette.text.primary,
        },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          color: theme.palette.text.primary,
        },
      },
      series: [
        {
          name: translate("fuelEconomyReport.morning", { _: "Morning" }),
          type: "line",
          data: morningValues,
          smooth: true,
          itemStyle: {
            color: "#a855f7", // Purple
          },
          symbol: "circle",
          symbolSize: 6,
        },
        {
          name: translate("fuelEconomyReport.noon", { _: "Noon" }),
          type: "line",
          data: noonValues,
          smooth: true,
          itemStyle: {
            color: "#ec4899", // Pink
          },
          symbol: "circle",
          symbolSize: 6,
        },
        {
          name: translate("fuelEconomyReport.afternoon", { _: "Afternoon" }),
          type: "line",
          data: afternoonValues,
          smooth: true,
          itemStyle: {
            color: "#3b82f6", // Blue
          },
          symbol: "circle",
          symbolSize: 6,
        },
        {
          name: translate("fuelEconomyReport.night", { _: "Night" }),
          type: "line",
          data: nightValues,
          smooth: true,
          itemStyle: {
            color: "#f97316", // Orange
          },
          symbol: "circle",
          symbolSize: 6,
        },
      ],
    };
  }, [
    data,
    fromDate,
    toDate,
    periodType,
    translate,
    theme.palette.text.primary,
  ]);

  return <ReactECharts style={{ height: 400, flex: 1 }} option={chartOption} />;
};

export default MultiSeriesLineChart;
